import { useEffect } from 'react';
/**
 * Custom hook that triggers a callback when focus moves outside of a specified element.
 *
 * This hook is useful for cases where you want to collapse a dropdown, close a modal, or hide an element when it loses focus.
 *
 * @param {Ref} ref - A React ref object pointing to the HTML element you want to monitor for focus changes.
 *                    The element must have focusable children (e.g., buttons, inputs, links) for this hook to work effectively.
 * @param {Callback} callback - A function that is invoked when the user tabs or moves focus outside the target element.
 * @param {boolean} state - A boolean representing the active state (e.g., whether the element is currently visible or open).
 *                          The hook will only listen for focus changes when this state is `true`.
 *
 * The hook:
 * - Listens for `focusin` events to detect when the user moves focus away from the element or its children.
 * - If the `state` is true and `ref.current` exists, and the newly focused element is outside the target element, the `callback` is invoked.
 * - If `state` or `ref.current` is false or null, the function exits early to avoid unintended behavior.
 * - The effect cleans up by removing the event listener when the component unmounts or if dependencies change.
 *
 * @example
 * const dropdownRef = useRef(null);
 * const [isOpen, setIsOpen] = useState(false);
 *
 * // Close dropdown when focus is lost
 * useFocusOutside(dropdownRef, () => setIsOpen(false), isOpen);
 *
 * return (
 *   <div ref={dropdownRef}>
 *     <button onClick={() => setIsOpen((prev) => !prev)}>
 *       Toggle Dropdown
 *     </button>
 *     {isOpen && (
 *       <ul>
 *         <li><a href="#">Item 1</a></li>
 *         <li><a href="#">Item 2</a></li>
 *         <li><a href="#">Item 3</a></li>
 *       </ul>
 *     )}
 *   </div>
 * );
 */

type Ref = React.RefObject<HTMLElement | null>;
type Callback = () => void;

export const useFocusOutside = (ref: Ref, callback: Callback, state: boolean) => {
  useEffect(() => {
    if (!ref.current || !state) return;

    const handleFocusOutside = (e: FocusEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        callback();
      }
    };

    document.addEventListener('focusin', handleFocusOutside);

    return () => {
      document.removeEventListener('focusin', handleFocusOutside);
    };
  }, [ref, callback, state]);
};
